<template>
    <div class="notes-editor">
        <span class="title mb-3">{{ $translate('notes') }}</span>

        <ul ref="notes_list" class="notes-list">
            <li v-for="note in notes" :key="note._id" class="note-container mt-3 mb-2 pb-2">
                <b-avatar
                    :title="getFullName(note.author)"
                    :src="note.author.imageUrl"
                    size="md"
                    class="mr-3 mt-1"
                />

                <i
                    v-if="note.author._id === user._id"
                    :title="$translate('delete')"
                    class="delete-button fa fa-trash-o p-1 ml-2"
                    @click="deleteNote(note)"
                />

                <div class="note-body">
                    <div class="message">{{ note.content }}</div>

                    <span class="date mt-1">
                        {{ $uiUtils.formatDate(note.createdAt, true) }}
                    </span>
                </div>
            </li>
        </ul>

        <form action="" class="create-note py-2" @submit.stop.prevent="saveNote">
            <b-avatar
                v-b-tooltip.hover
                :title="getFullName(user)"
                :src="user.imageUrl"
                size="md"
                class="mb-auto mr-3"
            />

            <textarea
                id="new-note"
                v-model="newNote"
                name="new-note"
                rows="2"
                cols="1"
                class="create-note-container px-2 p-1"
                @focus="$store.commit('timelapse/noteIsEdited', true)"
                @blur="$store.commit('timelapse/noteIsEdited', false)"
            ></textarea>

            <b-button type="submit" variant="primary" class="add-note-button ml-3">
                {{ $translate('add') }}
            </b-button>
        </form>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import UIUtils from '../../../business/utils/UIUtils'
import BackendProvider from '../../../business/services/BackendProvider'

export default {
    name: 'NotesEditor',
    data: () => ({
        newNote: ''
    }),
    computed: {
        ...mapState({
            focusedWell: state => state.timelapse.focusedWell,
            user: state => state.common.user,
            selectedTenant: state => state.common.selectedTenant
        }),
        notes() {
            return (this.focusedWell && this.focusedWell.notes) || []
        }
    },
    methods: {
        getFullName(person) {
            return `${person.firstName} ${person.lastName}`
        },
        async saveNote() {
            if (!this.newNote.trim().length) {
                return
            }

            UIUtils.showLoadingMask()
            this.$log.actionEvent('add_note')

            const note = {
                content: this.newNote,
                entityName: 'well',
                relatedDocId: this.focusedWell._id,
                authorId: this.user._id,
                tenantId: this.selectedTenant._id
            }

            await BackendProvider.addNote(note)
            this.focusedWell.notes = await BackendProvider.fetchNotes(this.focusedWell._id)

            UIUtils.hideLoadingMask()

            // reset form
            this.newNote = ''
            this.scrollUp()
        },
        deleteNote(note) {
            this.$uiUtils.showModal(
                'yesNo',
                data => {
                    data.yesNoHeader = 'delete_note'
                    data.yesNoMessage = 'delete_note_warning'
                },
                async () => {
                    UIUtils.showLoadingMask()
                    await BackendProvider.deleteNote(note)
                    UIUtils.hideLoadingMask()

                    this.focusedWell.notes.remove(note)
                    this.scrollUp()
                }
            )
        },
        scrollUp() {
            this.$nextTick(() => {
                this.$refs.notes_list.scrollTo(0, 999999)
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.notes-editor {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    background-color: $dark-3;

    .title {
        color: $light;
    }

    .notes-list {
        padding-inline-start: 0;
        overflow-x: hidden;
        overflow-y: auto;
        display: flex;
        flex-direction: column;

        .note-container {
            height: fit-content;
            position: relative;
            display: flex;
            border-bottom: $border-narrow-dark-dashed;

            .delete-button {
                position: absolute;
                right: 1rem;
                opacity: 0;
                transition: 0.2s all;
                cursor: pointer;
            }

            .note-body {
                display: flex;
                flex-direction: column;

                .message {
                    height: fit-content;
                    max-width: 19rem;
                }
                .date {
                    color: $light;
                    font-size: 10pt;
                }
            }

            &:hover {
                .delete-button {
                    opacity: 1;
                }
            }
        }
    }

    .create-note {
        width: 100%;
        display: flex;
        align-items: center;
        margin-bottom: auto;

        .create-note-container {
            margin: 0;
            width: 100%;
            overflow-y: auto;
            color: $light-3;
            background-color: $dark-4;
            box-shadow: inset 0 0 2px 0 $shadow !important;
            border: none;
            border-radius: $border-radius-lg;
        }

        .add-note-button {
            // overrides .focused-well-page .btn
            height: initial;
        }
    }
}
</style>
